import { formatDate, formatInteger, type ReferrerPartner, type ReferrerSubmitter } from '@brimo/shared';
import { Button, Dialog, Table, TableSkeleton, type TableColumn } from '../../../../ds/components';
import { QueryError } from '../users/OrganizationPage';
import { useReferrerSubmitters } from './api';
import styles from './ReferrerPartnersPage.module.css';

/**
 * „Einreicher anzeigen" (owner decision 02.11.2026): read-only list of the people who submitted through the partner's
 * link — the partner's own sales partners use the same link and are told apart by the e-mail of step 5.
 */

const TRACKS = ['minmax(0,1.1fr)', 'minmax(0,1.4fr)', '140px', '110px', '100px'];

const columns: TableColumn<ReferrerSubmitter>[] = [
  {
    id: 'name',
    header: 'Name',
    track: TRACKS[0],
    cell: (row) => <span className={styles.name}>{row.name}</span>,
  },
  {
    id: 'email',
    header: 'E-Mail',
    track: TRACKS[1],
    cell: (row) => <span className={styles.muted}>{row.email}</span>,
  },
  {
    id: 'phone',
    header: 'Telefon',
    track: TRACKS[2],
    cell: (row) =>
      row.phone ? (
        <span className={styles.muted}>{row.phone}</span>
      ) : (
        <span className={styles.subtle}>—</span>
      ),
  },
  {
    id: 'submissions',
    header: 'Einreichungen',
    track: TRACKS[3],
    numeric: true,
    cell: (row) => <span className={styles.count}>{formatInteger(row.submissions)}</span>,
  },
  {
    id: 'last',
    header: 'Zuletzt',
    track: TRACKS[4],
    cell: (row) => <span className={styles.muted}>{formatDate(row.lastSubmissionAt)}</span>,
  },
];

export function ReferrerSubmittersDialog({
  partner,
  onClose,
}: {
  partner: ReferrerPartner | null;
  onClose: () => void;
}) {
  const submitters = useReferrerSubmitters(partner?.id ?? null);
  return (
    <Dialog
      open={partner !== null}
      onClose={onClose}
      title="Einreicher"
      subtitle={partner?.name}
      width={760}
      footer={
        <Button variant="secondary" onClick={onClose}>
          Schließen
        </Button>
      }
    >
      {submitters.isError ? <QueryError error={submitters.error} /> : null}
      {submitters.isPending && partner !== null ? <TableSkeleton columns={TRACKS} rows={3} /> : null}
      {submitters.data ? (
        <Table
          label={`Einreicher von ${partner?.name ?? ''}`}
          columns={columns}
          rows={submitters.data}
          rowKey={(row) => row.key}
          density="compact"
          emptyText="Über diesen Link wurde noch nichts eingereicht."
        />
      ) : null}
    </Dialog>
  );
}
